"use client";

import React, { useState } from "react";
import { useRouter } from "next/navigation";
import Image from "next/image";
import { Star, ShoppingBag, Check, Loader2 } from "lucide-react";
import { useCart } from "@/context/CartContext";

export default function ProductCard({ product }: { product: any }) {
  const router = useRouter();
  const { addToCart, openCart } = useCart();
  const [added, setAdded] = useState(false);
  const [isAdding, setIsAdding] = useState(false);

  // 🌟 ডিটেইলস পেজে নেভিগেশন (লিংক ছাড়া, যাতে কোনো nesting error না হয়)
  const handleCardClick = () => {
    const target = product?.slug || product?.id;
    if (target) {
      router.push(`/products/${target}`);
    }
  };

  // 🌟 সরাসরি কার্টে অ্যাড (ProductDetailView-এর মতোই নির্ভরযোগ্য)
  const handleAdd = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation(); // কার্ডের ক্লিকে পেজ যাতে ওপেন না হয়

    if (!product || isAdding) return;
    setIsAdding(true);

    try {
      if (addToCart) {
        addToCart(product, 1);
      }
      if (openCart) {
        openCart();
      }

      setAdded(true);
      setTimeout(() => {
        setAdded(false);
        setIsAdding(false);
      }, 1200);
    } catch (err) {
      console.error("Cart Add Error:", err);
      setIsAdding(false);
    }
  };

  const currentPrice = Number(product?.discount_price || product?.sale_price || product?.price || 0);
  const regularPrice = Number(product?.compare_price || product?.regular_price || 0);
  const discountPercent =
    product?.discount_percentage ||
    (regularPrice > currentPrice && currentPrice > 0
      ? Math.round(((regularPrice - currentPrice) / regularPrice) * 100)
      : 0);

  const imgUrl =
    product?.primary_image ||
    product?.image ||
    (Array.isArray(product?.images) && product.images[0]?.image) ||
    (Array.isArray(product?.images) && typeof product.images[0] === "string" ? product.images[0] : null) ||
    product?.thumbnail ||
    "https://images.unsplash.com/photo-1542838132-92c53300491e?auto=format&fit=crop&w=400&q=80";

  return (
    <div 
      suppressHydrationWarning
      className="group bg-white border border-slate-100 rounded-3xl p-4 shadow-sm hover:shadow-md transition-all flex flex-col justify-between relative overflow-hidden"
    >
      {/* Discount Badge */}
      {discountPercent > 0 && (
        <span className="absolute top-3 left-3 bg-[#059669] text-white text-[10px] font-bold px-2 py-0.5 rounded-full z-10 pointer-events-none">
          {discountPercent}% off
        </span>
      )}

      {/* ক্লিকেবল প্রোডাক্ট ছবি ও টাইটেল সেকশন */}
      <div 
        onClick={handleCardClick}
        className="block space-y-3 cursor-pointer select-none"
      >
        <div className="relative aspect-square w-full rounded-2xl bg-[#F8FAFC] overflow-hidden flex items-center justify-center">
          <Image
            src={imgUrl}
            alt={product?.name || product?.title || "Product"}
            fill
            unoptimized
            className="object-contain p-3 group-hover:scale-105 transition-transform duration-300 pointer-events-none"
          />
        </div>

        <div className="space-y-1">
          <div className="flex items-center justify-between text-[11px] text-slate-400">
            <span>{product?.category_name || "General"}</span>
            <div className="flex items-center gap-0.5 text-amber-400 font-bold">
              <Star className="w-3 h-3 fill-amber-400" />
              <span>{product?.rating || "5.0"}</span>
            </div>
          </div>

          <h3 className="text-xs sm:text-sm font-bold text-slate-900 group-hover:text-[#059669] transition-colors line-clamp-1">
            {product?.name || product?.title}
          </h3>
          <p className="text-[11px] text-slate-400">{product?.unit || "1 pc"}</p>
        </div>
      </div>

      {/* প্রাইস এবং ইন্ডিপেন্ডেন্ট অ্যাড টু কার্ট বাটন */}
      <div className="flex items-center justify-between pt-3 mt-2 border-t border-slate-50 relative z-20">
        <div className="flex items-baseline gap-1.5">
          <span className="text-sm font-black text-slate-900">
            ৳{currentPrice.toLocaleString()}
          </span>
          {regularPrice > currentPrice && (
            <span className="text-[11px] text-slate-400 line-through">
              ৳{regularPrice.toLocaleString()}
            </span>
          )}
        </div>

        {/* 🌟 নিশ্চিত ক্লিকেবল বাটন */}
        <button
          type="button"
          onClick={handleAdd}
          disabled={isAdding}
          className={`relative z-30 pointer-events-auto px-3.5 py-1.5 rounded-full text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer shadow-xs select-none active:scale-95 ${
            added
              ? "bg-[#059669] text-white"
              : "bg-emerald-50 hover:bg-[#059669] text-[#059669] hover:text-white border border-emerald-200 hover:border-transparent"
          }`}
        >
          {isAdding ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : added ? (
            <>
              <Check className="w-3.5 h-3.5" />
              <span>Added</span>
            </>
          ) : (
            <>
              <ShoppingBag className="w-3.5 h-3.5" />
              <span>Add</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
}